import * as React from "react";
import {
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";

export type AppMode = "references" | "draft";

export interface AppModeToggleProps {
  mode: AppMode;
  onChange: (mode: AppMode) => void;
  isAssembly: boolean;
}

const modes: { value: AppMode; label: string; icon: React.ReactNode; beta?: boolean }[] = [
  { value: "draft", label: "Draft", icon: <EditNoteIcon />, beta: true },
  { value: "references", label: "Assembly", icon: <DescriptionOutlinedIcon /> },
];

const AppModeToggle = ({ mode, onChange, isAssembly }: AppModeToggleProps) => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <IconButton
        size="small"
        onClick={() => setOpen(true)}
        title="Switch mode"
        sx={{ color: "text.secondary" }}
      >
        <MenuIcon fontSize="small" />
      </IconButton>
      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 220, pt: 2 } }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, pb: 1, color: "text.secondary" }}>
          Mode
        </Typography>
        <List disablePadding>
          {modes.map((m) => {
            const disabled = m.value === "references" && !isAssembly;
            return (
              <Tooltip
                key={m.value}
                title={disabled ? "Only available for assembly documents" : ""}
                placement="top"
              >
                <span>
                  <ListItemButton
                    selected={mode === m.value}
                    disabled={disabled}
                    onClick={() => {
                      onChange(m.value);
                      setOpen(false);
                    }}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      mb: 0.5,
                      "&.Mui-selected": {
                        bgcolor: "primary.light",
                        color: "primary.dark",
                        "& .MuiListItemIcon-root": { color: "primary.dark" },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{m.icon}</ListItemIcon>
                    <ListItemText primary={m.label} primaryTypographyProps={{ fontWeight: 500 }} />
                    {m.beta && (
                      <Typography variant="caption" sx={{ ml: 1, color: "text.secondary" }}>
                        Beta
                      </Typography>
                    )}
                  </ListItemButton>
                </span>
              </Tooltip>
            );
          })}
        </List>
      </Drawer>
    </>
  );
};

export default AppModeToggle;
